const RELOAD_GUARD_KEY = 'nuxt-build-meta-reload-once'
const BUILD_META_PATH = '/_nuxt/builds/meta/'

const EMPTY_MATCHER = {
  static: {},
  wildcard: {},
  dynamic: {},
}

const getRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

const isBuildMetaUrl = (url: string) => url.includes(BUILD_META_PATH)

const shouldRecover = (url: string, status?: number) => {
  if (!isBuildMetaUrl(url)) return false
  if (status === undefined) return true
  return status === 404 || status >= 500
}

const extractBuildId = (url: string) => {
  try {
    const pathname = new URL(url, window.location.origin).pathname
    const file = pathname.split('/').pop() || ''
    return file.replace(/\.json$/i, '') || 'stale'
  } catch {
    return 'stale'
  }
}

const createSyntheticMetaResponse = (url: string) => {
  const body = {
    id: extractBuildId(url),
    timestamp: Date.now(),
    matcher: EMPTY_MATCHER,
    prerendered: [] as string[],
  }

  return new Response(JSON.stringify(body), {
    status: 200,
    statusText: 'OK',
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

const reloadOnce = (url: string, reason: string) => {
  if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return

  sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
  console.warn(`[nuxt-build-meta] ${reason}; serving fallback manifest and reloading once: ${url}`)

  // Cache-bust HTML so Cloudflare/browser prefer a fresh document + buildId.
  const next = new URL(window.location.href)
  next.searchParams.set('_nuxt_build', String(Date.now()))
  window.location.replace(next.toString())
}

export default defineNuxtPlugin({
  name: 'build-meta-recovery',
  enforce: 'pre',
  setup() {
    if (!process.client) return

    // After a recovery reload, drop the cache-bust query so shared URLs stay clean.
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') {
      const url = new URL(window.location.href)
      if (url.searchParams.has('_nuxt_build')) {
        url.searchParams.delete('_nuxt_build')
        window.history.replaceState(window.history.state, '', url.toString())
      }
    }

    const originalFetch = window.fetch.bind(window)

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const requestUrl = getRequestUrl(input)

      try {
        const response = await originalFetch(input as RequestInfo, init)

        if (isBuildMetaUrl(requestUrl) && response.ok) {
          sessionStorage.removeItem(RELOAD_GUARD_KEY)
        }

        if (shouldRecover(requestUrl, response.status)) {
          reloadOnce(requestUrl, `meta fetch returned ${response.status}`)
          return createSyntheticMetaResponse(requestUrl)
        }

        return response
      } catch (error) {
        if (shouldRecover(requestUrl)) {
          reloadOnce(requestUrl, 'meta fetch threw an error')
          return createSyntheticMetaResponse(requestUrl)
        }

        throw error
      }
    }
  },
})
